import { Button, Input } from './ui';

export type DraftPollOption = {
  id: string;
  label: string;
};

export type DraftPollQuestion = {
  id: string;
  question: string;
  options: DraftPollOption[];
};

const MAX_QUESTIONS = 5;
const MAX_OPTIONS = 8;

export function createDraftPollQuestion(index: number): DraftPollQuestion {
  return {
    id: `draft-q-${index + 1}-${Date.now()}`,
    question: '',
    options: [
      { id: `draft-o-${index + 1}-1-${Date.now()}`, label: '' },
      { id: `draft-o-${index + 1}-2-${Date.now()}`, label: '' }
    ]
  };
}

export function normalizeDraftPollQuestions(questions: DraftPollQuestion[]) {
  return questions
    .map((question) => ({
      question: question.question.trim(),
      options: question.options.map((option) => option.label.trim()).filter(Boolean)
    }))
    .filter((question) => question.question || question.options.length);
}

export function draftPollIsValid(questions: DraftPollQuestion[]) {
  const normalized = normalizeDraftPollQuestions(questions);
  return normalized.length > 0 && normalized.every((question) => question.question && question.options.length >= 2);
}

export function DiscussionPollComposer(props: {
  questions: DraftPollQuestion[];
  onChange: (questions: DraftPollQuestion[]) => void;
}) {
  const { questions, onChange } = props;

  function updateQuestion(questionId: string, value: string) {
    onChange(questions.map((question) => (question.id === questionId ? { ...question, question: value } : question)));
  }

  function updateOption(questionId: string, optionId: string, value: string) {
    onChange(
      questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              options: question.options.map((option) =>
                option.id === optionId ? { ...option, label: value } : option
              )
            }
          : question
      )
    );
  }

  function addQuestion() {
    if (questions.length >= MAX_QUESTIONS) return;
    onChange([...questions, createDraftPollQuestion(questions.length)]);
  }

  function removeQuestion(questionId: string) {
    onChange(questions.filter((question) => question.id !== questionId));
  }

  function addOption(questionId: string) {
    onChange(
      questions.map((question) => {
        if (question.id !== questionId || question.options.length >= MAX_OPTIONS) return question;
        return {
          ...question,
          options: [
            ...question.options,
            {
              id: `${question.id}-o-${question.options.length + 1}-${Date.now()}`,
              label: ''
            }
          ]
        };
      })
    );
  }

  function removeOption(questionId: string, optionId: string) {
    onChange(
      questions.map((question) => {
        if (question.id !== questionId || question.options.length <= 2) return question;
        return {
          ...question,
          options: question.options.filter((option) => option.id !== optionId)
        };
      })
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-zinc-900">Poll</div>
          <div className="mt-0.5 text-xs text-zinc-600">
            Members answer every question once. Results show after their response is submitted.
          </div>
        </div>
        <Button variant="ghost" onClick={() => onChange([])}>
          Remove poll
        </Button>
      </div>

      <div className="mt-3 space-y-3">
        {questions.map((question, questionIndex) => (
          <div key={question.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-zinc-700">Question {questionIndex + 1}</div>
              {questions.length > 1 ? (
                <Button variant="ghost" onClick={() => removeQuestion(question.id)}>
                  Remove
                </Button>
              ) : null}
            </div>

            <Input
              value={question.question}
              onChange={(event) => updateQuestion(question.id, event.target.value)}
              placeholder="Question"
            />

            <div className="mt-2 space-y-2">
              {question.options.map((option, optionIndex) => (
                <div key={option.id} className="flex items-center gap-2">
                  <Input
                    value={option.label}
                    onChange={(event) => updateOption(question.id, option.id, event.target.value)}
                    placeholder={`Option ${optionIndex + 1}`}
                  />
                  {question.options.length > 2 ? (
                    <Button variant="ghost" onClick={() => removeOption(question.id, option.id)}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>

            {question.options.length < MAX_OPTIONS ? (
              <div className="mt-2">
                <Button variant="outline" onClick={() => addOption(question.id)}>
                  Add option
                </Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {questions.length < MAX_QUESTIONS ? (
        <div className="mt-3">
          <Button variant="outline" onClick={addQuestion}>
            Add question
          </Button>
        </div>
      ) : null}
    </div>
  );
}
